<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);

const form = reactive({ name: '', domain: '', description: '' });

const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.name = '';
  form.domain = '';
  form.description = '';
};

const open = (company = {}) => {
  form.name = company.name || '';
  form.domain = company.domain || '';
  form.description = company.description || '';
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    name: form.name.trim(),
    domain: form.domain.trim() || null,
    description: form.description.trim() || null,
  });
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    content-class="!gap-5 !rounded-xl !border !border-auth-border !bg-auth-surface !text-auth-text !shadow-none !backdrop-blur-none dark:!border-auth-border-dark dark:!bg-auth-surface-dark dark:!text-auth-text-dark"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-5">
      <div class="flex flex-col items-start gap-2">
        <span
          class="py-1 text-base font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ t('COMPANIES.CREATE.TITLE') }}
        </span>
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="form.name"
            :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME')"
            :disabled="isLoading"
            custom-input-class="h-10 !rounded-xl !border-auth-border !bg-auth-soft !px-3 !py-2 !text-auth-text placeholder:!text-auth-muted focus:!outline-auth-action dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:placeholder:!text-auth-muted-dark dark:focus:!outline-auth-accent"
            autofocus
          />
          <Input
            v-model="form.domain"
            :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN')"
            :disabled="isLoading"
            custom-input-class="h-10 !rounded-xl !border-auth-border !bg-auth-soft !px-3 !py-2 !text-auth-text placeholder:!text-auth-muted focus:!outline-auth-action dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:placeholder:!text-auth-muted-dark dark:focus:!outline-auth-accent"
          />
        </div>
      </div>
      <TextArea
        v-model="form.description"
        :placeholder="t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')"
        :disabled="isLoading"
        :max-length="280"
        class="w-full"
        custom-text-area-wrapper-class="!rounded-xl !border-auth-border !bg-auth-soft dark:!border-auth-border-dark dark:!bg-auth-soft-dark focus-within:!border-auth-action dark:focus-within:!border-auth-accent"
        custom-text-area-class="!text-auth-text placeholder:!text-auth-muted dark:!text-auth-text-dark dark:placeholder:!text-auth-muted-dark"
        show-character-count
        auto-height
      />
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="!h-10 !rounded-xl !border-auth-border !bg-auth-soft !px-4 !text-auth-text hover:!bg-auth-border hover:!no-underline dark:!border-auth-border-dark dark:!bg-auth-soft-dark dark:!text-auth-text-dark dark:hover:!bg-auth-border-dark"
          @click="closeDialog"
        />
        <Button
          :label="t('COMPANIES.CREATE.ACTIONS.SAVE')"
          color="blue"
          type="submit"
          class="!h-10 !rounded-xl !bg-auth-action !px-4 !text-white hover:!bg-auth-action-hover dark:!bg-auth-accent dark:!text-auth-brand dark:hover:!bg-auth-accent-hover"
          :disabled="isFormInvalid || isLoading"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
</template>
